import { useAuth } from "@/src/contexts/auth.context";
import { useAllBlocksPermissionsQuery } from "@/src/services/uam/permissions.blocks.api";
import { PERMISSIONS, type PermissionResource } from "@/src/constants/permissions.constants";
import { useMemo } from "react";

const normalizeRole = (role?: string | null) =>
  (role ?? "").toLowerCase().replace(/[\s_-]/g, "");


export const usePermissions = () => {
  const { roles, isSuperAdmin } = useAuth();
  const { data, isLoading, isError } = useAllBlocksPermissionsQuery();

  const userRoles = useMemo(() => roles.map(normalizeRole), [roles]);

  // permission name → set of normalized roles that hold it.
  const rolesByPermission = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const permission of data?.data ?? []) {
      map.set(
        permission.name,
        new Set((permission.roles ?? []).map(normalizeRole)),
      );
    }
    return map;
  }, [data]);

  const can = (permissionName: string): boolean => {
    if (isSuperAdmin) return true;
    if (isLoading || isError || !data) return true; // fail-open
    const allowedRoles = rolesByPermission.get(permissionName);
    if (!allowedRoles) return true; // permission not defined → unrestricted
    return userRoles.some((role) => allowedRoles.has(role));
  };

  // Resource-scoped helpers, e.g. canCreate("post"), canView("news").
  const canView = (resource: PermissionResource) =>
    can(PERMISSIONS[resource].view);
  const canCreate = (resource: PermissionResource) =>
    can(PERMISSIONS[resource].create);
  const canUpdate = (resource: PermissionResource) =>
    can(PERMISSIONS[resource].update);
  const canDelete = (resource: PermissionResource) =>
    can(PERMISSIONS[resource].delete);

  return {
    can,
    canView,
    canCreate,
    canUpdate,
    canDelete,
    isLoading,
  };
};
